import React, { useCallback, useMemo } from "react"
import ReactGridLayout, {
  Responsive,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout"
import type {
  Layout,
  LayoutItem,
  ReactGridLayoutProps,
} from "react-grid-layout"
import "react-grid-layout/css/styles.css"
import "react-resizable/css/styles.css"
import { QC_BUILDER_DRAG_TYPE } from "stores/queryBuilderStore"
import {
  createTile,
  clampLayout,
  DashboardDocument,
  DEFAULT_TILE_SIZES,
  GRID_COLUMNS,
  MIN_TILE_H,
  MIN_TILE_W,
  Tile,
  TileType,
  TILE_TYPES,
  TileLayout,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  selectActivePage,
} from "features/Dashboard/runtime/dashboardStore"
import TileChrome from "features/Dashboard/components/Tiles/TileChrome.component"
import { DRAG_TILE_TYPE } from "features/Dashboard/components/PageTabs/TilePalette.component"

const ROW_HEIGHT = 40
const MARGIN: [number, number] = [12, 12]

/**
 * Whether a drag event points inside a tile. A drag over text can report the
 * text node itself as its target, so the check starts from its parent.
 */
const overTile = (e: Event): boolean => {
  const target = e.target
  const element =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null
  return Boolean(element?.closest(".dq-grid-item"))
}

/**
 * The grid accepts a palette drag anywhere and a variable drag only on empty
 * space: over a tile the variable belongs to that tile (TileChrome handles
 * it), so the grid shows no placeholder there. Anything else is refused.
 */
const acceptDrag = (e: DragEvent): false | undefined => {
  const types = Array.from(e.dataTransfer?.types ?? [])
  if (types.includes(DRAG_TILE_TYPE)) return undefined
  if (types.includes(QC_BUILDER_DRAG_TYPE)) {
    return overTile(e) ? false : undefined
  }
  return false
}

// Stable config objects, so the grid does not rebuild them on every render.
const BUILD_GRID: ReactGridLayoutProps["gridConfig"] = {
  cols: GRID_COLUMNS,
  rowHeight: ROW_HEIGHT,
  margin: MARGIN,
}
const BUILD_DRAG: ReactGridLayoutProps["dragConfig"] = {
  enabled: true,
  handle: ".dq-tile-handle",
  cancel: ".dq-tile-nodrag",
}
const BUILD_RESIZE: ReactGridLayoutProps["resizeConfig"] = {
  enabled: true,
  handles: ["se"],
}
const BUILD_DROP: ReactGridLayoutProps["dropConfig"] = {
  enabled: true,
  defaultItem: DEFAULT_TILE_SIZES.table,
  onDragOver: acceptDrag,
}
const VIEW_DRAG: ReactGridLayoutProps["dragConfig"] = { enabled: false }
const VIEW_RESIZE: ReactGridLayoutProps["resizeConfig"] = { enabled: false }
const VIEW_BREAKPOINTS = { lg: 1024, md: 640, sm: 0 }
const VIEW_COLS = { lg: 12, md: 6, sm: 1 }

const toLayout = (tiles: Tile[]): Layout =>
  tiles.map((t) => ({
    i: t.id,
    ...t.layout,
    minW: MIN_TILE_W,
    minH: MIN_TILE_H,
  }))

/**
 * `doc` with every page's tiles where the Build grid puts them: moved up
 * into free space, as the grid does when it shows a page (and records as an
 * edit). The same document when nothing moves. For a whole document loaded
 * from elsewhere (the assistant), so showing it is not a further edit.
 */
export const settleLayouts = (doc: DashboardDocument): DashboardDocument => {
  let moved = false
  const pages = doc.pages.map((page) => {
    const compacted = new Map(
      verticalCompactor
        .compact(toLayout(page.tiles), GRID_COLUMNS)
        .map((item) => [item.i, clampLayout(item)])
    )
    let pageMoved = false
    const tiles = page.tiles.map((t) => {
      const n = compacted.get(t.id)
      if (
        !n ||
        (n.x === t.layout.x &&
          n.y === t.layout.y &&
          n.w === t.layout.w &&
          n.h === t.layout.h)
      ) {
        return t
      }
      pageMoved = true
      return { ...t, layout: n }
    })
    if (!pageMoved) return page
    moved = true
    return { ...page, tiles }
  })
  return moved ? { ...doc, pages } : doc
}

/** Six columns: halve x and w. One column: stack in reading order. */
export const responsiveLayouts = (
  tiles: Tile[]
): { lg: Layout; md: Layout; sm: Layout } => {
  const sorted = [...tiles].sort(
    (a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x
  )
  let y = 0
  const sm = sorted.map((t) => {
    const item = { i: t.id, x: 0, y, w: 1, h: t.layout.h }
    y += t.layout.h
    return item
  })
  const md = tiles.map((t) => ({
    i: t.id,
    x: Math.floor(t.layout.x / 2),
    y: t.layout.y,
    w: Math.max(1, Math.round(t.layout.w / 2)),
    h: t.layout.h,
  }))
  return { lg: toLayout(tiles), md, sm }
}

/**
 * The active page's tiles. Build mode: a 12-column grid, tiles dragged by
 * their header and resized from the corner, with palette and variable drops.
 * View mode: responsive (12, 6 or 1 columns), nothing moves.
 */
const DashboardGrid: React.FC = () => {
  const page = useDashboardStore(selectActivePage)
  const mode = useDashboardStore((s) => s.mode)
  const setTileLayouts = useDashboardStore((s) => s.setTileLayouts)
  const addTile = useDashboardStore((s) => s.addTile)
  const selectTile = useDashboardStore((s) => s.selectTile)
  const { width, containerRef, mounted } = useContainerWidth({
    measureBeforeMount: true,
  })
  const tiles = useMemo(() => page?.tiles ?? [], [page])

  const onLayoutChange = useCallback(
    (layout: Layout) => {
      if (!page) return
      const next: Record<string, TileLayout> = {}
      layout.forEach((item) => {
        next[item.i] = clampLayout({
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        })
      })
      const changed = tiles.some((t) => {
        const n = next[t.id]
        return (
          n &&
          (n.x !== t.layout.x ||
            n.y !== t.layout.y ||
            n.w !== t.layout.w ||
            n.h !== t.layout.h)
        )
      })
      if (changed) setTileLayouts(page.id, next)
    },
    [page, tiles, setTileLayouts]
  )

  const onDrop = useCallback(
    (_layout: Layout, item: LayoutItem | undefined, e: Event) => {
      if (!page || !item) return
      const dt = (e as DragEvent).dataTransfer
      const tileType = dt?.getData(DRAG_TILE_TYPE)
      const variable = dt?.getData(QC_BUILDER_DRAG_TYPE)
      const at = { x: item.x, y: item.y }
      if (tileType && (TILE_TYPES as string[]).includes(tileType)) {
        const type = tileType as TileType
        addTile(page.id, createTile(type, { ...at, ...DEFAULT_TILE_SIZES[type] }))
      } else if (variable && !overTile(e)) {
        // A variable dropped inside a tile belongs to that tile (TileChrome
        // took it). The drop still bubbles to the grid, and the grid can
        // still hold a placeholder then, so the target decides, not the
        // placeholder.
        const tile = createTile("table", { ...at, ...DEFAULT_TILE_SIZES.table })
        if (isDataTile(tile) && tile.source.kind === "builder") {
          tile.source = { ...tile.source, rows: [variable] }
        }
        addTile(page.id, tile)
      }
    },
    [page, addTile]
  )

  const children = useMemo(
    () =>
      tiles.map((t) => (
        <div key={t.id} className="dq-grid-item">
          <TileChrome tile={t} pageId={page?.id ?? ""} />
        </div>
      )),
    [tiles, page?.id]
  )
  const layouts = useMemo(() => responsiveLayouts(tiles), [tiles])

  // A click on the page itself (not on a tile) clears the selection. The
  // grid's own element covers the page, so it counts as the page too.
  const onBackgroundClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement
    if (
      target === e.currentTarget ||
      target.classList.contains("react-grid-layout")
    ) {
      selectTile(null)
    }
  }

  // The container always renders so the width observer attaches on mount.
  return (
    <div ref={containerRef} className="dq-grid" onClick={onBackgroundClick}>
      {page && mounted && mode === "build" && (
        <ReactGridLayout
          width={width}
          layout={layouts.lg}
          compactor={verticalCompactor}
          gridConfig={BUILD_GRID}
          dragConfig={BUILD_DRAG}
          resizeConfig={BUILD_RESIZE}
          dropConfig={BUILD_DROP}
          onLayoutChange={onLayoutChange}
          onDrop={onDrop}
        >
          {children}
        </ReactGridLayout>
      )}
      {page && mounted && mode === "view" && (
        <Responsive
          width={width}
          layouts={layouts}
          breakpoints={VIEW_BREAKPOINTS}
          cols={VIEW_COLS}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          dragConfig={VIEW_DRAG}
          resizeConfig={VIEW_RESIZE}
          compactor={verticalCompactor}
        >
          {children}
        </Responsive>
      )}
      {page && tiles.length === 0 && mode === "build" && (
        <p className="dq-grid-empty">Drag a tile type or a variable here.</p>
      )}
    </div>
  )
}

export default DashboardGrid
